import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ShipmentDirection } from '../../../@posturinn/client-react';
import { PAYMENT_ERROR } from '../../../constants/actions/paymentActions';
import myPostWrapper from '../../../hoc/myPostWrapper';
import usePaymentErrorFromQuery from '../../../hooks/usePaymentErrorFromQuery';
import useShipments from '../../../hooks/useShipments';
import IMyShipmentsViewModel from '../../../interfaces/myShipments/myShipments';
import IPaymentReducer from '../../../interfaces/reducers/paymentReducer';
import { IStore } from '../../../reducers';
import { H1 } from '../../base/typography';
import * as StyledModals from '../modals/myShipmentsModals.styles';
import * as StyledMyShipments from './myShipments.styles';
import ShipmentList from './shipmentList';
import styled from 'styled-components';
import Button from '../../base/button';
import { ReactComponent as IconEye } from '../../../icons/eye.svg';
import { ReactComponent as IconEyeClosed } from '../../../icons/eye-closed.svg';

interface Props {
	page: IMyShipmentsViewModel;
}

const MyShipments: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const maxPerPage = 5;

	const [paginationCountActive, setPaginationCountActive] = useState(1);
	const [paginationCountDelivered, setPaginationCountDelivered] = useState(1);
	const [selectedOption, setSelectedOption]: [number, (x: number) => void] = useState(0);
	const [includeHidden, setIncludeHidden] = useState<boolean>(false);
	const cashTimeMs = 1000;

	const shipmentsToUse = useShipments(
		paginationCountActive,
		paginationCountDelivered,
		selectedOption === 0 ? ShipmentDirection.Inbound : ShipmentDirection.Outbound,
		cashTimeMs,
		includeHidden
	);

	const incoming = () => {
		setPaginationCountActive(1);
		setPaginationCountDelivered(1);
		setSelectedOption(0);
	};

	const outgoing = () => {
		setPaginationCountActive(1);
		setPaginationCountDelivered(1);
		setSelectedOption(1);
	};

	const changePage = (val, deliveredList) => {
		if (deliveredList) setPaginationCountDelivered(val + 1);
		else setPaginationCountActive(val + 1);
	};

	usePaymentErrorFromQuery(error => ({ type: PAYMENT_ERROR, payload: error }));

	const paymentReducer: IPaymentReducer = useSelector<IStore, IPaymentReducer>(store => store.paymentReducer);
	const { payError } = paymentReducer;

	return (
		<div>
			<StyledMyShipments.HeaderMain>
				<H1>{t('navigation.shipments')}</H1>
			</StyledMyShipments.HeaderMain>
			<StyledMyShipments.ContentTabs>
				<StyledMyShipments.ContentTab active={selectedOption === 0} onClick={() => incoming()}>
					{t('page-overview.incomingShipments')}
				</StyledMyShipments.ContentTab>
				<StyledMyShipments.ContentTab active={selectedOption === 1} onClick={() => outgoing()}>
					{t('page-overview.outgoingShipments')}
				</StyledMyShipments.ContentTab>
			</StyledMyShipments.ContentTabs>

			{payError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					{payError}
				</StyledModals.ErrorMessage>
			)}

			{/* IN TRANSIT SHIPMENTS LIST */}
			<Grid>
				<StyledMyShipments.Heading>{t('page-overview.inTransit')}</StyledMyShipments.Heading>
				<Button ignoreSvgFill onClick={()=>setIncludeHidden(!includeHidden)}>
					{ includeHidden
						? <><IconEye /> {t('page-overview.hideHidden')}</>
						: <><IconEyeClosed /> {t('page-overview.showHidden')}</>
					}
				</Button>
			</Grid>
			<ShipmentList
				shipments={shipmentsToUse.activeShipments}
				delivered={false}
				paginationCount={paginationCountActive}
				maxPerPage={maxPerPage}
				changePage={changePage}
				includeHidden={includeHidden}
			/>

			{/* DELIVERED SHIPMENTS LIST */}
			<StyledMyShipments.Heading>{t('page-overview.delivered')}</StyledMyShipments.Heading>
			<ShipmentList
				shipments={shipmentsToUse.deliveredShipments}
				delivered
				paginationCount={paginationCountDelivered}
				maxPerPage={maxPerPage}
				changePage={changePage}
				includeHidden={includeHidden}
			/>
		</div>
	);
};

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr auto;
	align-items: center;
`;


export default myPostWrapper(MyShipments);
